/**
 * useScrollAwareHeader — 스크롤 방향에 따라 헤더 표시/숨김
 *
 * - 스크롤 다운: 헤더 숨김
 * - 스크롤 업: 헤더 표시
 * - 페이지 최상단(threshold 이내): 항상 표시
 * - 헤더 내부 포커스: 강제 표시 (접근성)
 * - passive scroll + requestAnimationFrame으로 성능 최적화
 */
import { useEffect, useRef, useState } from 'react';

interface Options {
  /** 숨김 시작 최소 스크롤 픽셀 (기본 80) */
  threshold?: number;
  /** 미세 진동 무시 임계값 (기본 5) */
  velocityThreshold?: number;
}

export function useScrollAwareHeader({
  threshold = 80,
  velocityThreshold = 5,
}: Options = {}) {
  const [visible, setVisible] = useState(true);
  const lastY = useRef(0);
  const rafId = useRef(0);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => {
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const delta = currentY - lastY.current;

        // 최상단 근처에서는 항상 표시
        if (currentY < threshold) {
          setVisible(true);
          lastY.current = currentY;
          return;
        }

        // 미세 진동 무시
        if (Math.abs(delta) < velocityThreshold) return;

        setVisible(delta < 0); // 위로 스크롤 = 표시
        lastY.current = currentY;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(rafId.current);
    };
  }, [threshold, velocityThreshold]);

  // 헤더 내 포커스 시 강제 표시 (접근성)
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const show = () => setVisible(true);
    el.addEventListener('focusin', show);
    return () => el.removeEventListener('focusin', show);
  }, []);

  return { visible, headerRef };
}
